// Enhanced main.js - Single entry point for all Vue components
import Vue from 'vue';
// Component registry with dynamic imports for better code splitting
const components = {
    // Cart components
    'cart': () => import('./components/cart/Cart.vue'),
    'cart-checkout': () => import('./components/cart/Checkout.vue'),
    // Quote components
    'quote-form': () => import('./quote/form.vue'),
    // Picture frames
    'designer': () => import('./picture-frames/designer.vue'),
    'value-frames': () => import('./picture-frames/value.vue'),
    'standard-frames-to-fit': () => import('./standard-size-picture-frames/to-fit.vue'),
    // DIY Materials
    'diy-unit': () => import('./materials/diy-framing/Unit.vue'),
    'diy-sheet': () => import('./materials/diy-framing/Sheet.vue'),
    'diy-length': () => import('./materials/diy-framing/Length.vue'),
    'diy-roll': () => import('./materials/diy-framing/Roll.vue'),
    // CTS Mounts
    'cts-samples': () => import('./materials/cts-mounts/ColourSamples.vue'),
    'cts-bespoke': () => import('./materials/cts-mounts/bespokeCTS.vue'),
    'cts-standard': () => import('./materials/cts-mounts/standardCTS.vue'),
    // Wholesale
    'wholesale-products': () => import('./wholesale/Products.vue'),
    'wholesale-mount-kits': () => import('./wholesale/MountKits.vue'),
    // Clearance
    'clearance-moulding': () => import('./clearance/moulding.vue'),
    // Ranges
    'ranges-search': () => import('./ranges/search.vue'),
    // Customer service
    'customer-feedback': () => import('./components/Feedback.vue'),
};
// Enhanced auto-discovery function using data attributes
async function mountComponents() {
    // Find all elements with data-vue-component attribute
    const dataElements = document.querySelectorAll('[data-vue-component]');
    for (const element of dataElements) {
        await mountSingleComponent(element, element.dataset.vueComponent);
    }
}
// Mount a single component with error handling
async function mountSingleComponent(element, componentName, selectorInfo = '') {
    if (!components[componentName]) {
        console.warn(`Component "${componentName}" not found in registry ${selectorInfo}`);
        return false;
    }
    try {
        // Dynamic import of the component
        const componentModule = await components[componentName]();
        const Component = componentModule.default || componentModule;
        // Parse additional props from data attributes (data-prop-*)
        const dataProps = extractDataProps(element);
        // Mount the Vue instance
        new Vue({
            el: element,
            render: function (createElement) {
                return createElement(Component, {
                    props: dataProps
                });
            },
        });
        return true;
    }
    catch (error) {
        console.error(`Failed to load component ${componentName}:`, error);
        return false;
    }
}
// Extract props from data-prop-* attributes
function extractDataProps(element) {
    const props = {};
    for (const key in element.dataset) {
        if (key.startsWith('prop')) {
            const propName = key
                .replace('prop', '')
                .replace(/^[A-Z]/, letter => letter.toLowerCase())
                .replace(/[A-Z]/g, letter => '-' + letter.toLowerCase());
            let value = element.dataset[key];
            // Try to parse as JSON for complex props
            try {
                if (value && (value.startsWith('{') || value.startsWith('[') || value === 'true' || value === 'false')) {
                    value = JSON.parse(value);
                }
            }
            catch (e) {
                // Keep as string if JSON parsing fails
            }
            props[propName] = value;
        }
    }
    return props;
}
// Initialize all components
async function initializeVueComponents() {
    try {
        // Mount data-attribute components first (highest priority)
        await mountComponents();
    }
    catch (error) {
        console.error('❌ Error during Vue component initialization:', error);
    }
}
// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initializeVueComponents();
    });
}
else {
    initializeVueComponents();
}
// Export for potential external usage
export { components, mountSingleComponent, initializeVueComponents };
